import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, TrendingUp, TrendingDown, Users, BarChart3, Calendar } from 'lucide-react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const PipelineHealth = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/.netlify/functions/pipeline-health');
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || `HTTP ${res.status}`);
      }
      setData(await res.json());
    } catch (err) {
      console.error('Pipeline health fetch failed:', err);
      setError(err.message);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="chart-card chart-full" style={{ padding: '2rem', textAlign: 'center' }}>
        <RefreshCw size={20} className="spinning" style={{ marginBottom: '0.5rem' }} />
        <div style={{ color: 'rgba(199, 210, 254, 0.6)', fontSize: '0.85rem' }}>Loading pipeline health data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="chart-card chart-full" style={{ padding: '1.5rem', textAlign: 'center' }}>
        <div style={{ color: '#EF4444', marginBottom: '0.5rem' }}>Pipeline health unavailable</div>
        <div style={{ color: 'rgba(199, 210, 254, 0.5)', fontSize: '0.8rem', marginBottom: '0.75rem' }}>{error}</div>
        <button onClick={fetchData} className="refresh-button"><RefreshCw size={14} /> Retry</button>
      </div>
    );
  }

  if (!data) return null;

  const changeColor = data.overallChange > 0 ? '#4ADE80' : data.overallChange < 0 ? '#EF4444' : 'rgba(199,210,254,0.6)';
  const ChangeIcon = data.overallChange >= 0 ? TrendingUp : TrendingDown;

  return (
    <div className="chart-card chart-full">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
        <div>
          <h3 style={{ color: '#fff', fontSize: '1.05rem', fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Users size={18} style={{ color: '#818CF8' }} />
            Pipeline Health — Eligible to Hire
          </h3>
          <p style={{ color: 'rgba(199, 210, 254, 0.5)', fontSize: '0.75rem', margin: '0.2rem 0 0' }}>
            Enrolled + Test Passed + Not Yet Hired/Dropped — snapshot comparison
          </p>
        </div>
        <button onClick={fetchData} style={{
          display: 'flex', alignItems: 'center', gap: '0.3rem', padding: '0.3rem 0.6rem',
          fontSize: '0.72rem', background: 'rgba(0,190,168,0.15)', border: '1px solid rgba(0,190,168,0.3)',
          color: '#4ADE80', borderRadius: '5px', cursor: 'pointer',
        }}>
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      {/* KPI Cards */}
      <div className="forecast-metrics-row" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
        <div className="forecast-metric">
          <div className="forecast-metric-value" style={{ color: '#818CF8' }}>{data.today.total.toLocaleString()}</div>
          <div className="forecast-metric-label">Eligible Today</div>
          <div className="forecast-metric-sub">{data.today.date}</div>
        </div>
        <div className="forecast-metric">
          <div className="forecast-metric-value" style={{ color: '#60A5FA' }}>{data.yearAgo.total.toLocaleString()}</div>
          <div className="forecast-metric-label">Eligible 1 Year Ago</div>
          <div className="forecast-metric-sub">{data.yearAgo.date}</div>
        </div>
        <div className="forecast-metric">
          <div className="forecast-metric-value" style={{ color: changeColor, display: 'flex', alignItems: 'center', gap: '0.3rem', justifyContent: 'center' }}>
            <ChangeIcon size={20} />
            {data.overallChange !== null ? `${data.overallChange > 0 ? '+' : ''}${data.overallChange}%` : '—'}
          </div>
          <div className="forecast-metric-label">YoY Change</div>
          <div className="forecast-metric-sub">{data.today.total - data.yearAgo.total >= 0 ? '+' : ''}{(data.today.total - data.yearAgo.total).toLocaleString()} candidates</div>
        </div>
      </div>

      {/* Monthly Trend */}
      {data.monthlyTrend && data.monthlyTrend.length > 0 && (
        <div style={{ marginBottom: '1.25rem' }}>
          <h4 style={{ color: 'rgba(199, 210, 254, 0.8)', fontSize: '0.85rem', fontWeight: 600, margin: '0 0 0.75rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <Calendar size={15} /> Eligible Pool — 13-Month Trend
          </h4>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={data.monthlyTrend} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
              <XAxis dataKey="label" stroke="rgba(199,210,254,0.4)" fontSize={11} />
              <YAxis stroke="rgba(199,210,254,0.4)" fontSize={11} />
              <Tooltip
                contentStyle={{ backgroundColor: 'rgba(30, 27, 75, 0.95)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px' }}
                labelStyle={{ color: '#E0E7FF' }}
                formatter={(value) => [value.toLocaleString(), 'Eligible']}
              />
              <Line type="monotone" dataKey="total" stroke="#818CF8" strokeWidth={2} dot={{ r: 3, fill: '#818CF8' }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* State Comparison Table */}
      {data.stateComparison && data.stateComparison.length > 0 && (
        <div>
          <h4 style={{ color: 'rgba(199, 210, 254, 0.8)', fontSize: '0.85rem', fontWeight: 600, margin: '0 0 0.5rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <BarChart3 size={15} /> By State — Today vs 1 Year Ago
          </h4>
          <div style={{ fontSize: '0.78rem', maxHeight: '350px', overflowY: 'auto' }}>
            <div style={{
              display: 'grid', gridTemplateColumns: '1fr 80px 80px 80px 80px',
              padding: '0.4rem 0.6rem', fontWeight: 600, fontSize: '0.68rem',
              color: 'rgba(199,210,254,0.5)', textTransform: 'uppercase', letterSpacing: '0.5px',
              borderBottom: '1px solid rgba(255,255,255,0.08)',
              position: 'sticky', top: 0, background: 'rgba(15, 23, 42, 0.95)',
            }}>
              <span>State</span>
              <span style={{ textAlign: 'center' }}>Today</span>
              <span style={{ textAlign: 'center' }}>1yr Ago</span>
              <span style={{ textAlign: 'center' }}>Change</span>
              <span style={{ textAlign: 'center' }}>YoY %</span>
            </div>
            {data.stateComparison.map(row => {
              const diff = row.current - row.prior;
              const diffColor = diff > 0 ? '#4ADE80' : diff < 0 ? '#EF4444' : 'rgba(199,210,254,0.4)';
              const pctColor = row.change > 0 ? '#4ADE80' : row.change < 0 ? '#EF4444' : 'rgba(199,210,254,0.4)';
              return (
                <div key={row.state} style={{
                  display: 'grid', gridTemplateColumns: '1fr 80px 80px 80px 80px',
                  padding: '0.35rem 0.6rem', borderBottom: '1px solid rgba(255,255,255,0.03)', alignItems: 'center',
                }}>
                  <span style={{ color: 'rgba(199,210,254,0.9)', fontWeight: 600 }}>{row.state}</span>
                  <span style={{ textAlign: 'center', color: '#818CF8' }}>{row.current}</span>
                  <span style={{ textAlign: 'center', color: '#60A5FA' }}>{row.prior}</span>
                  <span style={{ textAlign: 'center', color: diffColor, fontWeight: 600 }}>
                    {diff > 0 ? '+' : ''}{diff}
                  </span>
                  <span style={{ textAlign: 'center', color: pctColor, fontWeight: 700 }}>
                    {row.change !== null ? `${row.change > 0 ? '+' : ''}${row.change}%` : '—'}
                  </span>
                </div>
              );
            })}
            {/* Totals row */}
            <div style={{
              display: 'grid', gridTemplateColumns: '1fr 80px 80px 80px 80px',
              padding: '0.5rem 0.6rem', borderTop: '2px solid rgba(255,255,255,0.1)',
              fontWeight: 700, fontSize: '0.8rem',
            }}>
              <span style={{ color: '#fff' }}>TOTAL</span>
              <span style={{ textAlign: 'center', color: '#818CF8' }}>{data.today.total}</span>
              <span style={{ textAlign: 'center', color: '#60A5FA' }}>{data.yearAgo.total}</span>
              <span style={{ textAlign: 'center', color: changeColor }}>
                {data.today.total - data.yearAgo.total >= 0 ? '+' : ''}{data.today.total - data.yearAgo.total}
              </span>
              <span style={{ textAlign: 'center', color: changeColor }}>
                {data.overallChange !== null ? `${data.overallChange > 0 ? '+' : ''}${data.overallChange}%` : '—'}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PipelineHealth;
